import axios, { AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { z } from 'zod';
import { apiUrl } from '../common/constants';
import LocalStorageSvc, {
  JWT_KEY
} from '../common/localStorageSvc';
import useAuthStore from '../datastore/useAuth';
import requestManager from '../requestManager';

interface NormalizedAxiosError {
  status: number;
  statusText: string;
  data: {
    message?: string;
    issues?: Array<{
      componentName?: string;
      availableQuantity?: number;
      unit?: string;
      requiredQuantity?: number;
      productName?: string;
      reason?: string;
    }>;
  } | string | null;
}

const ErrorIssueSchema = z.object({
  componentName: z.string().optional(),
  availableQuantity: z.number().optional(),
  unit: z.string().optional(),
  requiredQuantity: z.number().optional(),
  productName: z.string().optional(),
  reason: z.string().optional(),
}).passthrough();

const ErrorDataSchema = z.union([
  z.string(),
  z.object({
    message: z.string().optional(),
    issues: z.array(ErrorIssueSchema).optional(),
  }).passthrough()
]);

const getAxiosError = (error: any): NormalizedAxiosError | null => {
  if (isAxiosError(error) && error.response) {
    const parsedData = ErrorDataSchema.safeParse(error.response.data);
    return {
      status: error.response.status,
      statusText: error.response.statusText,
      data: parsedData.success ? parsedData.data : error.response.data || null,
    };
  }

  // Fallback for non-standard axios errors that might still have status
  if (typeof error === 'object' && error !== null && 'status' in error && 'statusText' in error) {
    const errObj = error as Record<string, string | number | object>;
    return {
      status: Number(errObj.status),
      statusText: String(errObj.statusText),
      data: (errObj.data as NormalizedAxiosError['data']) || null,
    };
  }
  return null;
}

const getAccountSuspendedError = (err: NormalizedAxiosError | null): string | null => {
  if (err?.status === 403 && typeof err.data === 'object' && err.data?.message === 'Account Suspended') {
    useAuthStore.getState().setSuspended()
    return 'Forbidden: Account Suspended'
  }
  return null
}

const getAxiosErrorMessage = (err: NormalizedAxiosError | null): string | null => {
  if (!err) {
    return null
  }

  // Handle case where error is a string (e.g. StatusCode(400, "Message"))
  if (typeof err.data === 'string') {
    return err.data;
  }

  if (err.data && typeof err.data === 'object' && err.data.message) {
    let msg = err.data.message;
    if (Array.isArray(err.data.issues)) {
      const issuesList = err.data.issues.map((issue) => {
        const componentName = issue.componentName || 'Unknown Component';
        const available = issue.availableQuantity ?? 0;
        const required = issue.requiredQuantity ?? 0;
        const unit = issue.unit || 'units';
        const productName = issue.productName || 'Unknown Product';
        if (issue.reason === 'SoldOut') {
          return `• ${productName} is sold out`;
        }
        return `• ${componentName}: ${available} ${unit} available, but ${required} ${unit} required for ${productName}`;
      }).join('\n');
      msg = `${msg}\n\n${issuesList}`;
    }
    return msg;
  }
  return err.statusText || null
}

export const handleError = (error: any): { message: string, suspended: boolean } => {
  const axError = getAxiosError(error)
  if (axError) {
    let message = getAccountSuspendedError(axError)
    if (message) {
      return { message, suspended: true }
    }
    message = getAxiosErrorMessage(axError)
    if (message) {
      return { message, suspended: false }
    }
  }

  // Fallback to standard error message if it exists
  if (error instanceof Error) {
    return { message: error.message, suspended: false };
  }

  return { message: 'Something went wrong', suspended: false }
}


const isExpired = (token: string): boolean => {
  const decoded = JSON.parse(atob(token.split('.')[1]));
  return decoded.exp < Date.now() / 1000;
};


const checkJwt = async (): Promise<string> => {
  let jwt = LocalStorageSvc.load<string>(JWT_KEY);
  if (jwt && !isExpired(jwt)) {
    return jwt
  }
  const response = await useAuthStore.getState().refreshToken()
  if (!response) {
    return ''
  }
  jwt = LocalStorageSvc.load<string>(JWT_KEY);
  return jwt || ''
}

axios.interceptors.request.use(
  async function (
    request: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> {
    if (request.url && request.url.includes('auth/refresh')) {
      return request;
    }
    const jwt = await checkJwt()
    if (jwt) {
      request.headers.set('Authorization', `Bearer ${jwt}`);
    }
    if (!(request.data instanceof FormData)) {
      request.headers.set('Content-Type', 'application/json');
    }
    return request;
  },
);

export class ApiError extends Error {
  status?: number;
  suspended?: boolean;

  constructor(message: string, status?: number, suspended?: boolean) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.suspended = suspended;
  }
}

// New generic request method
async function makeRequest<T = AxiosResponse>(
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  endpoint: string,
  data?: object | string | FormData | null
): Promise<T> {
  const url = `${apiUrl}${endpoint}`;

  const key = requestManager.getKey(
    url,
    data ? JSON.stringify(data) : undefined
  );

  try {
    const response = await requestManager.request<T>(`${method}:${key}`, () => {
      switch (method) {
        case 'get':
          return axios.get(url);
        case 'post':
          return axios.post(url, data);
        case 'put':
          return axios.put(url, data);
        case 'delete':
          return axios.delete(url);
        case 'patch':
          return axios.patch(url, data);
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
    });
    return response;
  } catch (error) {
    const normalized = handleError(error);
    throw new ApiError(
      normalized.message,
      isAxiosError(error) ? error.response?.status : undefined,
      normalized.suspended
    );
  }
}

export const getRequest = (endpoint: string) =>
  makeRequest('get', endpoint);

export const postRequest = (endpoint: string, data: object | string | FormData | null) =>
  makeRequest('post', endpoint, data);

export const putRequest = (endpoint: string, data: object | string | FormData | null) =>
  makeRequest('put', endpoint, data);

export const deleteRequest = (endpoint: string) =>
  makeRequest('delete', endpoint);

export const patchRequest = (endpoint: string, data: object | string | FormData | null) =>
  makeRequest('patch', endpoint, data);